import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
// FIX: Change from react-hot-toast to react-toastify
import { toast } from 'react-toastify';
import { FaUser, FaEnvelope, FaLock, FaPhoneAlt, FaEye, FaEyeSlash, } from 'react-icons/fa';
import Lottie from 'lottie-react';
import PublicNavbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import signupAnimation from '../../assets/animations/signup-animation.json';
import '../../styles/auth.css';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'password') {
      calculatePasswordStrength(value);
    }
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.match(/[a-z]+/)) strength++;
    if (password.match(/[A-Z]+/)) strength++;
    if (password.match(/[0-9]+/)) strength++;
    if (password.match(/[$@#&!]+/)) strength++;
    setPasswordStrength(strength);
  };

  const getStrengthText = () => {
    if (passwordStrength <= 2) return 'Weak';
    if (passwordStrength <= 4) return 'Medium';
    return 'Strong';
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 2) return '#dc3545';
    if (passwordStrength <= 4) return '#ffc107';
    return '#28a745';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordStrength < 3) {
      toast.error('Please use a stronger password');
      return;
    }
    
    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      const response = await register(registerData);
      toast.success(response.message || 'Registration successful! Please wait for admin approval.');
      // Add a small delay before navigation so user can see the success message
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PublicNavbar />
      
      <div className="auth-container">
        <div className="auth-row signup-row">
          {/* Left Side - Animation & Content */}
          <div className="auth-left signup-left">
            <div className="auth-animation">
              <Lottie 
                animationData={signupAnimation} 
                loop={true} 
                style={{ width: '100%', height: '300px' }}
              />
            </div>
            
            <div className="auth-content">
              <h2>Join Sensible Academy!</h2>
              <p>Start your journey towards a successful IT career</p>
              
              <div className="auth-features">
                <div className="feature">
                  <span className="feature-icon-auth">🎓</span>
                  <span>Industry Recognized Certificate</span>
                </div>
                <div className="feature">
                  <span className="feature-icon-auth">💼</span>
                  <span>100% Placement Assistance</span>
                </div>
                <div className="feature">
                  <span className="feature-icon-auth">👨‍🏫</span>
                  <span>Expert Instructors</span>
                </div>
                <div className="feature">
                  <span className="feature-icon-auth">📚</span>
                  <span>Lifetime Access to Materials</span>
                </div>
              </div>
              
              <div className="auth-offer">
                <span className="offer-badge">Limited Time Offer</span>
                <p>Get 20% off on course enrollment!</p>
              </div>
            </div>
          </div>

          {/* Right Side - Signup Form */}
          <div className="auth-right">
            <div className="auth-card">
              <div className="auth-header">
                <h3>Create New Account</h3>
                <p>Join thousands of successful students</p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Full Name</label>
                  <div className="input-icon">
                    <FaUser className="icon" />
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <div className="input-icon">
                    <FaEnvelope className="icon" />
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone Number</label>
                  <div className="input-icon">
                    <FaPhoneAlt className="icon" />
                    <input
                      type="tel"
                      name="phone"
                      className="form-control"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <div className="input-icon">
                    <FaLock className="icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="form-control"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      placeholder="Create a password"
                    />
                    <button 
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {formData.password && (
                    <div className="password-strength">
                      <div className="strength-bar">
                        <div 
                          className="strength-fill" 
                          style={{ 
                            width: `${(passwordStrength / 5) * 100}%`,
                            backgroundColor: getStrengthColor()
                          }}
                        />
                      </div>
                      <span className="strength-text" style={{ color: getStrengthColor() }}>
                        Password strength: {getStrengthText()}
                      </span>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Confirm Password</label>
                  <div className="input-icon">
                    <FaLock className="icon" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      className="form-control"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      placeholder="Confirm your password"
                    />
                    <button 
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="auth-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Creating Account...
                    </>
                  ) : 'Create Account'}
                </button>

              </form>

              <div className="auth-footer">
                <p>Already have an account? <Link to="/login">Login here</Link></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Signup;